import { Card, CardContent, CardHeader } from '@/components/ui/card';
import useReport from '@/hooks/report';
import TableCustomer from '@/components/table';
import { DatePicker } from '@/components/ui/date-picker';
import SelectComponent from '@/components/select';
import { Button } from '@/components/ui/button';
import { EyeIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getUsers } from '@/api/user';

const ListReport: React.FC = () => {
    const { isLoading, reports, handleSetQuery, query } = useReport();
    const navigate = useNavigate();

    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: () => getUsers({ page: 1, limit: -1 }),
    });

    const handleDateChange = (selectedDate: Date | undefined) => {
        if (selectedDate) {
            const date = new Date(selectedDate);
            date.setDate(date.getDate() + 1);
            handleSetQuery({ ...query, day: date.toISOString().split('T')[0] });
        } else {
            handleSetQuery({ ...query, day: '' });
        }
    };

    const handlePageChange = (newPage: number) => {
        if (!reports?.data || reports.data.length === 0) return;
        handleSetQuery({ ...query, page: newPage });
    };

    const columns = [
        {
            value: 'user.name',
            label: 'User name'
        },
        {
            value: 'report.description',
            label: 'Description'
        }
    ];

    return (
        <div className="flex flex-col w-full space-y-4 p-2 sm:p-4">
            <Card>
                <CardHeader>
                    <CardContent className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="w-full sm:w-auto">
                            <DatePicker
                                date={query.day ? new Date(query.day) : undefined}
                                onChange={(value) => handleDateChange(value)}
                            />
                        </div>

                        <div className="w-full sm:w-[200px]">
                            <SelectComponent
                                options={[{ label: '10', value: '10' }, { label: '25', value: '25' }, { label: '50', value: '50' }]}
                                onChange={(value) => handleSetQuery({ ...query, limit: Number(value), page: 1 })}
                                placeholder="Items per page"
                                defaultValue={query.limit.toString()}
                            />
                        </div>

                        <div className="w-full sm:w-[200px]">
                            <SelectComponent
                                options={[{ label: 'Select user "All"', value: 'all' }, ...(users?.users?.map((user) => ({ label: user.name, value: user._id || '' })) || [])]}
                                onChange={(value) => handleSetQuery({ ...query, uid: value, page: 1 })}
                                placeholder="Select user"
                                defaultValue={query.uid}
                            />
                        </div>
                    </CardContent>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        {reports?.data && reports.data.length > 0 ? (
                            <TableCustomer
                                columns={columns}
                                data={reports.data}
                                loading={isLoading}
                                pagination={{
                                    currentPage: query.page,
                                    totalPages: Math.ceil((reports?.total || 0) / (query.limit || 10)),
                                    totalItems: reports?.total || 0,
                                    itemsPerPage: query.limit || 10,
                                    onPageChange: handlePageChange
                                }}
                                countShow={60}
                                View={({ data }) => {
                                    return (
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className='border-none text-gray-900 hover:bg-gray-100'
                                            onClick={() => navigate(`/report/detail-report?id=${data.report._id}`)}
                                        >
                                            <EyeIcon className="w-4 h-4" />
                                        </Button>
                                    )
                                }}
                            />
                        ) : (
                            <div className="flex justify-center items-center h-[200px]">
                                <p className="text-muted-foreground text-sm sm:text-base">No reports found</p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ListReport;